import React, { ReactNode } from "react";
import { Alert, Button, Col, Container, Form, Row } from "react-bootstrap";
import { Sun, Moon, BookHalf, Funnel, FunnelFill } from "react-bootstrap-icons";
import { ResponsiveContainer, Tooltip, Treemap, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";

import { getAnalyticsWidget, createExportSchedule } from "../../../utils/Requests";
import { getAnalyticsBrandLabel, getDefaultDateRange, getWidgetTheme, replaceAnalyticsBrandTerminology } from "../../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions, WidgetTheme, DocsSidebarEntry } from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";
import { PopPeriodBanner, DataFreshnessBanner } from "../../../components/analytics";
import { DocsSidebar, ExportButton, RankedBarDatum, RankedBarWidget, ScheduleExportModal, WidgetCard } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import HeatmapMetricsTable from "../../../components/analytics/HeatmapMetricsTable";
import ContentBrandTreeTable from "../../../components/analytics/ContentBrandTreeTable";
import ContentGenreRadarWidget from "../widgets/ContentGenreRadarWidget";
import {
    GenreExplorerMetricsByTimePoint,
    GenreExplorerTimeView,
    METRIC_BY_TIME_OPTIONS,
    getDefaultMetricByTime,
    getMetricByTimeOption,
    toChartMetricValue,
    formatMetricByTimeValue
} from "./contentGenreExplorerMetricsByTime";
import Branding from "../../../config/Branding";
import Logger from "../../../utils/Logger";

type ContentDashboardProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type ChartDataPoint = {
    label: string;
    value: number;
    fill?: string;
};

type ChartCard = {
    key: string;
    docsKey: string;
    title: string;
    data: RankedBarDatum[];
    emptyMessage?: string;
    tooltipLabelFormatter?: (label: ReactNode) => ReactNode;
    tooltipValueFormatter?: (value: number, item: RankedBarDatum) => [string, string] | string;
    xAxisTickFormatter?: (value: number) => string;
    sortComparator?: (a: RankedBarDatum, b: RankedBarDatum) => number;
    xAxisScaleMode?: "linear" | "sqrt";
    presentation?: "standard" | "diverging";
    divergingDomain?: "balanced" | "positive";
    divergingMargin?: { left?: number; right?: number };
    valueLabelFormatter?: (value: number, item: RankedBarDatum) => string;
};

type ContentGenreActionsRow = Record<string, any>;
type ContentGenreTableRow = Record<string, any>;
type ContentBrandTableRow = Record<string, any>;
type ContentPageTableRow = Record<string, any>;
type ContentRailTableRow = Record<string, any>;
type ContentDeviceTableRow = Record<string, any>;
type RailMetricKey = "plays" | "views" | "users" | "avg_progress" | "plays_per_user" | "hours";
type TreemapScope = "top10" | "last10" | "all";

type SourceTreemapNode = {
    name: string;
    value: number;
    rawMetricValue: number;
    sourceLabel: string;
    plays: number;
    views: number;
    users: number;
    avgProgress: number | null;
    playsPerUser: number | null;
    hours: number;
};
type ContentEngagementMetricsResponseRow = Record<string, number | null | undefined>;

type ContentEngagementMetric = {
    key: string;
    label: string;
    value: number | null;
    changePercent: number | null;
    valueFormat?: "number" | "percent" | "decimal" | "durationHours";
    description?: string;
};
type ContentRisersFallersRow = Record<string, any>;

type WidgetLoadState = {
    isLoading: boolean;
    error: string | null;
};

type WidgetStateMap = Record<string, WidgetLoadState>;

const CONTENT_WIDGET_KEYS = [
    "content_engagement_metrics",
    "share_actions",
    "share_users",
    "share_content",
    "share_brands",
    "content_risers_fallers",
    "content_recommended_risers_fallers",
    "content_new_risers",
    "content_percentage_risers_fallers",
    "content_genre_table",
    "content_brand_table",
    "content_page_table",
    "content_rail_table",
    "content_device_table",
    "content_metrics_weekly",
    "content_metrics_dow"
];

const createInitialWidgetStates = (isLoading: boolean): WidgetStateMap => {
    return CONTENT_WIDGET_KEYS.reduce<WidgetStateMap>((acc, widgetKey) => {
        acc[widgetKey] = { isLoading, error: null };
        return acc;
    }, {});
};

type ContentDashboardState = {
    isLoading: boolean;
    error: string | null;
    widgetStates: WidgetStateMap;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    filtersCollapsed: boolean;
    isDarkTheme: boolean;
    genreActionsData: ChartDataPoint[];
    genreUsersData: ChartDataPoint[];
    shareContentData: ChartDataPoint[];
    shareBrandsData: ChartDataPoint[];
    contentEngagementMetrics: ContentEngagementMetric[];
    contentRisersFallersData: RankedBarDatum[];
    contentRecommendedRisersFallersData: RankedBarDatum[];
    contentNewRisersData: RankedBarDatum[];
    contentPercentageRisersFallersData: RankedBarDatum[];
    genreTableData: ContentGenreTableRow[];
    brandTableData: ContentBrandTableRow[];
    pageTableData: ContentPageTableRow[];
    railTableData: ContentRailTableRow[];
    deviceTableData: ContentDeviceTableRow[];
    videoStartWeeklyData: GenreExplorerMetricsByTimePoint[];
    videoStartDowData: GenreExplorerMetricsByTimePoint[];
    videoStartTimeView: GenreExplorerTimeView;
    videoStartMetric: string;
    isPageTableView: boolean;
    isRailTableView: boolean;
    isDeviceTableView: boolean;
    showEmptyPageCategories: boolean;
    showEmptyRailCategories: boolean;
    showEmptyDeviceCategories: boolean;
    pageScope: TreemapScope;
    railScope: TreemapScope;
    deviceScope: TreemapScope;
    selectedPageMetric: RailMetricKey;
    selectedRailMetric: RailMetricKey;
    selectedDeviceMetric: RailMetricKey;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
    showScheduleModal: boolean;
    editingSchedule: null;
};


const chartColors = ["#007bff", "#20c997", "#fd7e14", "#6f42c1", "#e83e8c", "#17a2b8"];
const CHART_PRIMARY_COLOR = Branding.primary;
const INDIGO_HUE = 257;
const RAIL_METRIC_OPTIONS: Array<{ key: RailMetricKey; label: string }> = [
    { key: "plays", label: "Plays" },
    { key: "views", label: "Views" },
    { key: "users", label: "Users" },
    { key: "avg_progress", label: "Avg Progress" },
    { key: "plays_per_user", label: "Plays Per User" },
    { key: "hours", label: "Hours" }
];

const SOURCE_SCOPE_OPTIONS: Array<{ key: TreemapScope; label: string }> = [
    { key: "top10", label: "Top 10" },
    { key: "last10", label: "Last 10" },
    { key: "all", label: "All" }
];

class ContentDashboard extends React.Component<ContentDashboardProps, ContentDashboardState> {
    private static readonly TREEMAP_HEIGHT = 560;
    private activeRequestId = 0;
    private videoStartWeeklyRequestId = 0;
    private videoStartDowRequestId = 0;
    private isMountedFlag = false;

    constructor(props: ContentDashboardProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();
        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandName: [],
            devicePlatform: []
        };

        this.state = {
            isLoading: false,
            error: null,
            widgetStates: createInitialWidgetStates(false),
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandNames: [],
                devicePlatforms: []
            },
            filtersCollapsed: false,
            isDarkTheme: true,
            genreActionsData: [],
            genreUsersData: [],
            shareContentData: [],
            shareBrandsData: [],
            contentEngagementMetrics: [],
            contentRisersFallersData: [],
            contentRecommendedRisersFallersData: [],
            contentNewRisersData: [],
            contentPercentageRisersFallersData: [],
            genreTableData: [],
            brandTableData: [],
            pageTableData: [],
            railTableData: [],
            deviceTableData: [],
            videoStartWeeklyData: [],
            videoStartDowData: [],
            videoStartTimeView: "dow",
            videoStartMetric: getDefaultMetricByTime(),
            isPageTableView: false,
            isRailTableView: false,
            isDeviceTableView: false,
            showEmptyPageCategories: true,
            showEmptyRailCategories: true,
            showEmptyDeviceCategories: true,
            pageScope: "top10",
            railScope: "top10",
            deviceScope: "top10",
            selectedPageMetric: "plays",
            selectedRailMetric: "plays",
            selectedDeviceMetric: "plays",
            sidebarOpen: false,
            sidebarEntries: [],
            showScheduleModal: false,
            editingSchedule: null
        };
    }

    componentDidMount(): void {
        this.isMountedFlag = true;
        this.fetchDashboardData();
    }

    componentWillUnmount(): void {
        this.isMountedFlag = false;
        this.activeRequestId += 1;
    }

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters }
        }), () => {
            this.fetchDashboardData();
        });
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private getExportData = (): ExportableData[] => {
        const { contentEngagementMetrics,
            genreActionsData,
            genreUsersData,
            shareContentData,
            shareBrandsData,
            contentRisersFallersData,
            contentRecommendedRisersFallersData,
            contentNewRisersData,
            contentPercentageRisersFallersData,
            appliedFilters
        } = this.state;
        const data: ExportableData[] = [];

        if (contentEngagementMetrics.length > 0) {
            data.push({
                title: "Content Engagement Metrics",
                columns: [
                    { key: "label", label: "Metric" },
                    { key: "value", label: "Value", format: "number" },
                    { key: "changePercent", label: "PoP Change %", format: "percent" }
                ],
                rows: contentEngagementMetrics.map(metric => ({
                    label: metric.label,
                    value: metric.value,
                    changePercent: metric.changePercent
                })),
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (genreActionsData.length > 0) {
            data.push({
                title: "Genre Share (Actions)",
                columns: [
                    { key: "label", label: "Genre" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: genreActionsData,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (genreUsersData.length > 0) {
            data.push({
                title: "Genre Share (Users)",
                columns: [
                    { key: "label", label: "Genre" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: genreUsersData,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (shareContentData.length > 0) {
            data.push({
                title: "Content Share",
                columns: [
                    { key: "label", label: "Content" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: shareContentData,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (shareBrandsData.length > 0) {
            data.push({
                title: replaceAnalyticsBrandTerminology("Brand Share", this.props.customerConfig),
                columns: [
                    { key: "label", label: getAnalyticsBrandLabel(this.props.customerConfig) },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: shareBrandsData,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        const riserWidgets: Array<{ title: string; rows: RankedBarDatum[]; metricLabel: string; format?: "percent" }> = [
            {
                title: replaceAnalyticsBrandTerminology("Top 10 Risers & Fallers in Plays", this.props.customerConfig),
                rows: contentRisersFallersData,
                metricLabel: "Start Difference"
            },
            {
                title: replaceAnalyticsBrandTerminology("Top 10 Risers & Fallers in Recommended Plays", this.props.customerConfig),
                rows: contentRecommendedRisersFallersData,
                metricLabel: "Recommended Start Difference"
            },
            {
                title: replaceAnalyticsBrandTerminology("Top 10 New Risers", this.props.customerConfig),
                rows: contentNewRisersData,
                metricLabel: "Current Starts"
            },
            {
                title: replaceAnalyticsBrandTerminology("Top 10 Percentage Risers & Fallers", this.props.customerConfig),
                rows: contentPercentageRisersFallersData,
                metricLabel: "Start Change %",
                format: "percent"
            }
        ];

        riserWidgets.forEach(widget => {
            if (widget.rows.length === 0) {
                return;
            }

            data.push({
                title: widget.title,
                columns: [
                    { key: "label", label: getAnalyticsBrandLabel(this.props.customerConfig) },
                    { key: "value", label: widget.metricLabel, format: widget.format === "percent" ? "percent" : "number" }
                ],
                rows: widget.rows,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        });

        return data;
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Content Dashboard",
            pageKey: "content_dashboard",
            customerSlug: this.props.customerConfig.slug,
            filters: this.state.appliedFilters,
            getData: this.getExportData,
            getContainerRef: () => null
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private buildSidebarEntry = (widgetKey: string, response: unknown): DocsSidebarEntry | null => {
        const meta = (response as any)?.widget;
        if (!meta) {
            return null;
        }

        return {
            widgetKey,
            displayName: meta.display_name,
            description: meta.description,
            schema: (response as any)?.schema
        };
    };

    private upsertSidebarEntry = (entries: DocsSidebarEntry[], entry: DocsSidebarEntry): DocsSidebarEntry[] => {
        const nextEntries = entries.filter(current => current.widgetKey !== entry.widgetKey);
        nextEntries.push(entry);
        return nextEntries;
    };

    private updateWidgetState = async (
        requestId: number,
        widgetKey: string,
        loadResponse: () => Promise<unknown>,
        applyResponse: (response: unknown) => Partial<ContentDashboardState>
    ): Promise<void> => {
        try {
            const response = await loadResponse();

            if (!this.isMountedFlag || requestId !== this.activeRequestId) {
                return;
            }

            const sidebarEntry = this.buildSidebarEntry(widgetKey, response);

            this.setState(prevState => {
                const widgetStates = {
                    ...prevState.widgetStates,
                    [widgetKey]: { isLoading: false, error: null }
                };

                return {
                    ...applyResponse(response),
                    sidebarEntries: sidebarEntry ? this.upsertSidebarEntry(prevState.sidebarEntries, sidebarEntry) : prevState.sidebarEntries,
                    widgetStates,
                    isLoading: Object.values(widgetStates).some(state => state.isLoading)
                } as ContentDashboardState;
            });
        } catch (error) {
            if (!this.isMountedFlag || requestId !== this.activeRequestId) {
                return;
            }

            Logger.error(`Error fetching ${widgetKey}`, error);
            this.setState(prevState => {
                const widgetStates = {
                    ...prevState.widgetStates,
                    [widgetKey]: { isLoading: false, error: "Failed to load widget data" }
                };

                return {
                    widgetStates,
                    isLoading: Object.values(widgetStates).some(state => state.isLoading)
                } as ContentDashboardState;
            });
        }
    };

    private mapContentGenreActionsToChartData = (
        rows: ContentGenreActionsRow[],
        metric: "action_count" | "user_count"
    ): ChartDataPoint[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mapped = rows.map((row: ContentGenreActionsRow, index: number) => {
            const name = row.genre || `Segment ${index + 1}`;
            const value = Number(row[metric] ?? 0);
            return {
                label: String(name),
                value,
                fill: chartColors[index % chartColors.length]
            };
        }).filter((item: ChartDataPoint) => Number.isFinite(item.value) && item.value > 0);

        return mapped.slice(0, 20);
    };

    private mapContentGenreCatalogueToChartData = (
        rows: ContentGenreActionsRow[],
        metric: "unique_things" | "unique_brands"
    ): ChartDataPoint[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mapped = rows.map((row: ContentGenreActionsRow, index: number) => {
            const name = row.genre || `Segment ${index + 1}`;
            const value = Number(row[metric] ?? 0);
            return {
                label: String(name),
                value,
                fill: chartColors[index % chartColors.length]
            };
        }).filter((item: ChartDataPoint) => Number.isFinite(item.value) && item.value > 0);

        return mapped.slice(0, 20);
    };

    private mapContentEngagementMetrics = (
        row: ContentEngagementMetricsResponseRow,
        schema?: Record<string, { description?: string }>
    ): ContentEngagementMetric[] => {
        const metricMap: Array<{
            key: string;
            label: string;
            changeKey: string;
            valueFormat?: "number" | "percent" | "decimal" | "durationHours";
        }> = [
            { key: "video_start", label: "Video Starts", changeKey: "video_start_pop_pct" },
            { key: "views", label: "Views", changeKey: "views_pop_pct" },
            { key: "users", label: "Users", changeKey: "users_pop_pct" },
            { key: "avg_percent_watched", label: "Avg % Watched", changeKey: "avg_percent_watched_pop_pct", valueFormat: "percent" },
            { key: "hours_watched", label: "Hours Watched", changeKey: "hours_watched_pop_pct", valueFormat: "decimal" },
            { key: "video_start_per_user", label: "Video Starts / User", changeKey: "video_start_per_user_pop_pct", valueFormat: "decimal" },
            { key: "session_conversion", label: "Session Conversion", changeKey: "session_conversion_pop_pct", valueFormat: "percent" }
        ];

        return metricMap
            .filter(metric => Object.prototype.hasOwnProperty.call(row, metric.key))
            .map(metric => {
                let value = row[metric.key] ?? null;

                if (metric.key === "session_conversion" && value !== null) {
                    value *= 100;
                }

                return {
                    key: metric.key,
                    label: metric.label,
                    value,
                    changePercent: row[metric.changeKey] ?? null,
                    valueFormat: metric.valueFormat,
                    description: schema?.[metric.key]?.description
                };
            });
    };

    private toNumberOrNull = (value: unknown): number | null => {
        if (typeof value === "number" && Number.isFinite(value)) {
            return value;
        }

        if (typeof value === "string") {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) {
                return parsed;
            }
        }

        return null;
    };

    private mapRisersFallersData = (
        rows: ContentRisersFallersRow[],
        metric: "video_start_diff" | "video_start_current" | "video_start_pop_pct",
        actualChangeMetric?: "video_start_diff"
    ): RankedBarDatum[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mapped: Array<RankedBarDatum | null> = rows
            .map((row: ContentRisersFallersRow) => {
                const value = this.toNumberOrNull(row?.[metric]);
                if (value === null) {
                    return null;
                }

                const actualChange = actualChangeMetric ? this.toNumberOrNull(row?.[actualChangeMetric]) : null;

                const label = String(row?.brand_name || row?.thing_brandname || "Unknown");
                const movement = String(row?.movement || "").toLowerCase();

                return {
                    label,
                    value,
                    fill: movement === "faller" || value < 0 ? "#ff5c7a" : "#4fd1c5",
                    actualChange: actualChange ?? undefined
                };
            });

        return mapped.filter((item): item is RankedBarDatum => item !== null);
    };

    private fetchDashboardData = async (): Promise<void> => {
        const { appliedFilters } = this.state;
        const requestId = ++this.activeRequestId;
        const actionsPromise = getAnalyticsWidget(
            this.props.customerConfig,
            "content_genre_actions",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        );

        this.setState({
            isLoading: true,
            error: null,
            widgetStates: createInitialWidgetStates(true),
            genreActionsData: [],
            genreUsersData: [],
            shareContentData: [],
            shareBrandsData: [],
            contentRisersFallersData: [],
            contentRecommendedRisersFallersData: [],
            contentNewRisersData: [],
            contentPercentageRisersFallersData: [],
            contentEngagementMetrics: [], genreTableData: [],
            brandTableData: [], pageTableData: [], railTableData: [], deviceTableData: [],
            videoStartWeeklyData: [],
            videoStartDowData: [],
            sidebarEntries: []
        });

        void this.updateWidgetState(requestId, "content_engagement_metrics", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_engagement_metrics",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => {
            const result = (response as any)?.result;
            const row = Array.isArray(result) ? result[0] : null;
            return {
                contentEngagementMetrics: row && typeof row === "object"
                    ? this.mapContentEngagementMetrics(row as ContentEngagementMetricsResponseRow, (response as any)?.schema)
                    : []
            };
        });

        void this.updateWidgetState(requestId, "share_actions", () => actionsPromise, (response: unknown) => {
            const rows: ContentGenreActionsRow[] = Array.isArray((response as any)?.result) ? (response as any).result : [];
            return {
                genreActionsData: this.mapContentGenreActionsToChartData(rows, "action_count")
            };
        });

        void this.updateWidgetState(requestId, "share_users", () => actionsPromise, (response: unknown) => {
            const rows: ContentGenreActionsRow[] = Array.isArray((response as any)?.result) ? (response as any).result : [];
            return {
                genreUsersData: this.mapContentGenreActionsToChartData(rows, "user_count")
            };
        });

        void this.updateWidgetState(requestId, "share_content", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_genre_catalogue"
        ), (response: unknown) => {
            const rows: ContentGenreActionsRow[] = Array.isArray((response as any)?.result) ? (response as any).result : [];
            return {
                shareContentData: this.mapContentGenreCatalogueToChartData(rows, "unique_things")
            };
        });

        void this.updateWidgetState(requestId, "share_brands", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_genre_catalogue"
        ), (response: unknown) => {
            const rows: ContentGenreActionsRow[] = Array.isArray((response as any)?.result) ? (response as any).result : [];
            return {
                shareBrandsData: this.mapContentGenreCatalogueToChartData(rows, "unique_brands")
            };
        });

        void this.updateWidgetState(requestId, "content_genre_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_genre_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            genreTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }));

        void this.updateWidgetState(requestId, "content_brand_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_brand_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            brandTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }));

        void this.updateWidgetState(requestId, "content_page_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_page_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            pageTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }));

        void this.updateWidgetState(requestId, "content_rail_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_rail_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            railTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }));

        void this.updateWidgetState(requestId, "content_device_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_device_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            deviceTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }));

        void this.updateWidgetState(requestId, "content_risers_fallers", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_risers_fallers",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            contentRisersFallersData: this.mapRisersFallersData(Array.isArray((response as any)?.result) ? (response as any).result : [], "video_start_diff")
        }));

        void this.updateWidgetState(requestId, "content_recommended_risers_fallers", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_recommended_risers_fallers",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            contentRecommendedRisersFallersData: this.mapRisersFallersData(Array.isArray((response as any)?.result) ? (response as any).result : [], "video_start_diff")
        }));

        void this.updateWidgetState(requestId, "content_new_risers", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_new_risers",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            contentNewRisersData: this.mapRisersFallersData(Array.isArray((response as any)?.result) ? (response as any).result : [], "video_start_current")
        }));

        void this.updateWidgetState(requestId, "content_percentage_risers_fallers", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_percentage_risers_fallers",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            contentPercentageRisersFallersData: this.mapRisersFallersData(
                Array.isArray((response as any)?.result) ? (response as any).result : [],
                "video_start_pop_pct",
                "video_start_diff"
            )
        }));

        const videoStartMetric = this.state.videoStartMetric;
        this.fetchVideoStartByTimeWidget("weekly", videoStartMetric);
        this.fetchVideoStartByTimeWidget("dow", videoStartMetric);

        const buildSidebar = (): void => {
            void requestId;
        };

        buildSidebar();
    };

    private renderBarCard = (chart: ChartCard, theme: WidgetTheme): ReactNode => {
        const { sidebarEntries, widgetStates } = this.state;
        const widgetState = widgetStates[chart.key] || { isLoading: false, error: null };

        return (
            <RankedBarWidget
                key={chart.key}
                title={chart.title}
                description={sidebarEntries.find(e => e.widgetKey === chart.docsKey)?.description}
                theme={theme}
                data={chart.data}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                emptyMessage={chart.emptyMessage}
                labelFormatter={(label: string) => label
                    .replace(/[\r\n]+/g, " ")
                    .replace(/\s+/g, " ")
                    .trim()}
                tooltipLabelFormatter={chart.tooltipLabelFormatter}
                tooltipValueFormatter={chart.tooltipValueFormatter}
                xAxisTickFormatter={chart.xAxisTickFormatter}
                sortComparator={chart.sortComparator}
                xAxisScaleMode={chart.xAxisScaleMode}
                presentation={chart.presentation}
                divergingDomain={chart.divergingDomain}
                divergingMargin={chart.divergingMargin}
                valueLabelFormatter={chart.valueLabelFormatter}
            />
        );
    };

    private getChartsConfig = (): ChartCard[] => {
        const {
            genreActionsData,
            genreUsersData,
            contentRisersFallersData,
            contentRecommendedRisersFallersData,
            contentNewRisersData,
            contentPercentageRisersFallersData
        } = this.state;

        return [
            {
                key: "share_actions",
                docsKey: "share_actions",
                title: "Share of Actions",
                data: genreActionsData,
                tooltipLabelFormatter: (label: ReactNode) => `Genre: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => {
                    const total = genreActionsData.reduce((sum, item) => sum + item.value, 0);
                    const percentage = total > 0 ? (value / total) * 100 : 0;
                    return [`${value.toLocaleString()} (${percentage.toFixed(1)}%)`, "Share of Actions"];
                }
            },
            {
                key: "share_users",
                docsKey: "share_users",
                title: "Share of Users",
                data: genreUsersData,
                tooltipLabelFormatter: (label: ReactNode) => `Genre: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => {
                    const total = genreUsersData.reduce((sum, item) => sum + item.value, 0);
                    const percentage = total > 0 ? (value / total) * 100 : 0;
                    return [`${value.toLocaleString()} (${percentage.toFixed(1)}%)`, "Share of Users"];
                }
            },
            {
                key: "content_risers_fallers",
                docsKey: "content_risers_fallers",
                title: replaceAnalyticsBrandTerminology("Top 10 Risers & Fallers in Plays", this.props.customerConfig),
                data: contentRisersFallersData,
                emptyMessage: replaceAnalyticsBrandTerminology("No risers/fallers data available for the selected date range.", this.props.customerConfig),
                tooltipLabelFormatter: (label: ReactNode) => `${getAnalyticsBrandLabel(this.props.customerConfig)}: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => [`${value.toLocaleString()}`, "Start Difference"],
                xAxisScaleMode: "sqrt",
                presentation: "diverging"
            },
            {
                key: "content_recommended_risers_fallers",
                docsKey: "content_recommended_risers_fallers",
                title: replaceAnalyticsBrandTerminology("Top 10 Risers & Fallers in Recommended Plays", this.props.customerConfig),
                data: contentRecommendedRisersFallersData,
                emptyMessage: replaceAnalyticsBrandTerminology("No recommendation-attributed risers/fallers data available for the selected date range.", this.props.customerConfig),
                tooltipLabelFormatter: (label: ReactNode) => `${getAnalyticsBrandLabel(this.props.customerConfig)}: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => [`${value.toLocaleString()}`, "Recommended Start Difference"],
                xAxisScaleMode: "sqrt",
                presentation: "diverging"
            },
            {
                key: "content_new_risers",
                docsKey: "content_new_risers",
                title: replaceAnalyticsBrandTerminology("Top 10 New Risers", this.props.customerConfig),
                data: contentNewRisersData,
                emptyMessage: replaceAnalyticsBrandTerminology("No new risers data available for the selected date range.", this.props.customerConfig),
                tooltipLabelFormatter: (label: ReactNode) => `${getAnalyticsBrandLabel(this.props.customerConfig)}: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => [`${value.toLocaleString()}`, "Current Starts"],
                xAxisScaleMode: "sqrt",
                presentation: "diverging",
                divergingDomain: "positive",
                divergingMargin: { left: 160 }
            },
            {
                key: "content_percentage_risers_fallers",
                docsKey: "content_percentage_risers_fallers",
                title: replaceAnalyticsBrandTerminology("Top 10 Percentage Risers & Fallers", this.props.customerConfig),
                data: contentPercentageRisersFallersData,
                emptyMessage: replaceAnalyticsBrandTerminology("No percentage risers/fallers data available for the selected date range.", this.props.customerConfig),
                tooltipLabelFormatter: (label: ReactNode) => `${getAnalyticsBrandLabel(this.props.customerConfig)}: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number, item: RankedBarDatum) => {
                    const actualChangeText = typeof item.actualChange === "number"
                        ? ` (${item.actualChange >= 0 ? "+" : ""}${item.actualChange.toLocaleString()} starts)`
                        : "";

                    return [`${value.toFixed(2)}%${actualChangeText}`, "Start Change %"];
                },
                xAxisTickFormatter: (value: number) => `${value.toFixed(0)}%`,
                xAxisScaleMode: "sqrt",
                presentation: "diverging",
                valueLabelFormatter: (value: number) => `${value.toFixed(0)}%`
            }
        ];
    };

    private getCatalogueChartsConfig = (): ChartCard[] => {
        const { shareContentData, shareBrandsData } = this.state;

        return [
            {
                key: "share_content",
                docsKey: "share_content",
                title: "Share of Content",
                data: shareContentData,
                tooltipLabelFormatter: (label: ReactNode) => `Genre: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => {
                    const total = shareContentData.reduce((sum, item) => sum + item.value, 0);
                    const percentage = total > 0 ? (value / total) * 100 : 0;
                    return [`${value.toLocaleString()} (${percentage.toFixed(1)}%)`, "Share of Content"];
                }
            },
            {
                key: "share_brands",
                docsKey: "share_brands",
                title: replaceAnalyticsBrandTerminology("Share of Brands", this.props.customerConfig),
                data: shareBrandsData,
                tooltipLabelFormatter: (label: ReactNode) => `Genre: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => {
                    const total = shareBrandsData.reduce((sum, item) => sum + item.value, 0);
                    const percentage = total > 0 ? (value / total) * 100 : 0;
                    return [`${value.toLocaleString()} (${percentage.toFixed(1)}%)`, replaceAnalyticsBrandTerminology("Share of Brands", this.props.customerConfig)];
                }
            }
        ];
    };

    private renderBrandTable = (theme: WidgetTheme): ReactNode => {
        const { brandTableData, sidebarEntries, widgetStates } = this.state;
        const isRevryCustomer = this.props.customerConfig.name.toLowerCase().includes("revry")
            || this.props.customerConfig.slug.toLowerCase().includes("revry");
        const hasHierarchyShape = brandTableData.some(row =>
            Object.prototype.hasOwnProperty.call(row, "channel_type")
            && Object.prototype.hasOwnProperty.call(row, "channel")
            && Object.prototype.hasOwnProperty.call(row, "user_country")
        );
        const useTreeTable = isRevryCustomer || hasHierarchyShape;
        const widgetState = widgetStates.content_brand_table || { isLoading: false, error: null };
        return (
            <WidgetCard
                title={replaceAnalyticsBrandTerminology("Brand Metrics", this.props.customerConfig)}
                description={sidebarEntries.find(e => e.widgetKey === "content_brand_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && brandTableData.length === 0}
                emptyMessage={replaceAnalyticsBrandTerminology("No brand table data available for the selected date range.", this.props.customerConfig)}
            >
                {useTreeTable ? (
                    <ContentBrandTreeTable
                        data={brandTableData}
                        emptyMessage={replaceAnalyticsBrandTerminology("No brand table data available", this.props.customerConfig)}
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        customerConfig={this.props.customerConfig}
                    />
                ) : (
                    <HeatmapMetricsTable
                        data={brandTableData}
                        emptyMessage={replaceAnalyticsBrandTerminology("No brand table data available", this.props.customerConfig)}
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        initialSortKey="plays"
                        preferredLeadingKeys={["thing_brandname", "brand_name", "brand"]}
                        customerConfig={this.props.customerConfig}
                    />
                )}
            </WidgetCard>
        );
    };

    private renderGenreTable = (theme: WidgetTheme): ReactNode => {
        const { genreTableData, sidebarEntries, widgetStates } = this.state;
        const widgetState = widgetStates.content_genre_table || { isLoading: false, error: null };
        return (
            <WidgetCard
                title="Genre Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "content_genre_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && genreTableData.length === 0}
                emptyMessage="No genre table data available for the selected date range."
            >
                <HeatmapMetricsTable
                    data={genreTableData}
                    emptyMessage="No genre table data available"
                    hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                    initialSortKey="plays"
                    preferredLeadingKeys={["genre", "genre_name", "name"]}
                    customerConfig={this.props.customerConfig}
                />
            </WidgetCard>
        );
    };

    private renderGenreRadarWidget = (theme: WidgetTheme): ReactNode => {
        const { appliedFilters } = this.state;
        return (
            <ContentGenreRadarWidget
                customerConfig={this.props.customerConfig}
                appliedFilters={appliedFilters}
                theme={theme}
            />
        );
    };

    private applyRankBasedTreemapWeights = (rows: SourceTreemapNode[]): SourceTreemapNode[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const sortedRows = [...rows].sort((a, b) => {
            if (b.rawMetricValue !== a.rawMetricValue) {
                return b.rawMetricValue - a.rawMetricValue;
            }

            return a.name.localeCompare(b.name);
        });

        const uniqueMetricValuesDesc = Array.from(new Set(sortedRows.map(row => row.rawMetricValue)));
        const maxLevels = 6;
        const levelCount = Math.min(maxLevels, uniqueMetricValuesDesc.length);
        const rankByValue = new Map<number, number>();
        uniqueMetricValuesDesc.forEach((value, index) => {
            rankByValue.set(value, index + 1);
        });

        return sortedRows.map(row => {
            const denseRank = rankByValue.get(row.rawMetricValue) ?? uniqueMetricValuesDesc.length;
            const level = Math.floor(((denseRank - 1) * levelCount) / uniqueMetricValuesDesc.length) + 1;
            const rankWeight = levelCount - level + 1;

            return {
                ...row,
                value: rankWeight
            };
        });
    };

    private getMetricValueFromRow = (row: Record<string, any>, metricKey: RailMetricKey): number => {
        const rawValue = Number(row[metricKey] ?? 0);
        return Number.isFinite(rawValue) ? rawValue : 0;
    };

    private getRowLabel = (row: Record<string, any>): string => {
        return String(
            row.source_page_rollup
            ?? row.source_page
            ?? row.source_rail
            ?? row.source_rail_rollup
            ?? row.device_platform
            ?? row.page
            ?? row.rail
            ?? row.device
            ?? row.name
            ?? ""
        );
    };

    private getScopedRowsByMetric = <T extends Record<string, any>>(rows: T[], metricKey: RailMetricKey, scope: TreemapScope, showEmptyCategories: boolean): T[] => {
        const rankedRows = rows
            .filter(row => showEmptyCategories || this.getMetricValueFromRow(row, metricKey) !== 0)
            .sort((a, b) => {
                const diff = this.getMetricValueFromRow(b, metricKey) - this.getMetricValueFromRow(a, metricKey);
                if (diff !== 0) {
                    return diff;
                }

                return this.getRowLabel(a).localeCompare(this.getRowLabel(b));
            });

        if (scope === "top10") {
            return rankedRows.slice(0, 10);
        }

        if (scope === "last10") {
            return rankedRows.slice(Math.max(0, rankedRows.length - 10));
        }

        return rankedRows;
    };

    private getPageTreemapData = (rows: ContentPageTableRow[], metricKey: RailMetricKey): SourceTreemapNode[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mappedRows = rows.map((row: ContentPageTableRow, index: number) => {
            const plays = Number(row.plays ?? 0);
            const views = Number(row.views ?? 0);
            const users = Number(row.users ?? 0);
            const hours = Number(row.hours ?? 0);
            const avgProgressRaw = row.avg_progress;
            const playsPerUserRaw = row.plays_per_user;
            const avgProgress = avgProgressRaw === null || avgProgressRaw === undefined ? null : Number(avgProgressRaw);
            const playsPerUser = playsPerUserRaw === null || playsPerUserRaw === undefined ? null : Number(playsPerUserRaw);
            const sourcePage = String(row.source_page_rollup ?? row.source_page ?? `Page ${index + 1}`);
            const metricValues: Record<RailMetricKey, number | null> = {
                plays: Number.isFinite(plays) ? plays : null,
                views: Number.isFinite(views) ? views : null,
                users: Number.isFinite(users) ? users : null,
                "avg_progress": Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                "plays_per_user": Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours: Number.isFinite(hours) ? hours : null
            };

            const metricValue = metricValues[metricKey];
            const rawMetricValue = metricValue === null ? 0 : Math.max(0, metricValue);

            return {
                name: sourcePage,
                value: rawMetricValue,
                rawMetricValue,
                sourceLabel: sourcePage,
                plays,
                views,
                users,
                avgProgress: Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                playsPerUser: Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours
            };
        });

        return this.applyRankBasedTreemapWeights(mappedRows);
    };

    private renderPageWidget = (theme: WidgetTheme): ReactNode => {
        const { pageTableData, sidebarEntries, widgetStates, isPageTableView, selectedPageMetric, pageScope, showEmptyPageCategories } = this.state;
        const widgetState = widgetStates.content_page_table || { isLoading: false, error: null };
        const filteredRows = this.getScopedRowsByMetric(pageTableData, selectedPageMetric, pageScope, showEmptyPageCategories);
        const treemapData = this.getPageTreemapData(filteredRows, selectedPageMetric);

        return (
            <WidgetCard
                title="Source Page Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "content_page_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && filteredRows.length === 0}
                emptyMessage="No source page table data available for the selected date range."
            >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", gap: "12px", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Metric</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={selectedPageMetric}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ selectedPageMetric: event.target.value as RailMetricKey })}
                            style={{
                                minWidth: "190px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {RAIL_METRIC_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Range</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={pageScope}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ pageScope: event.target.value as TreemapScope })}
                            style={{
                                minWidth: "120px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {SOURCE_SCOPE_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <Form.Check
                            type="switch"
                            id="show-empty-page-categories"
                            label="Show empty categories"
                            checked={showEmptyPageCategories}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ showEmptyPageCategories: event.target.checked })}
                            style={{ color: theme.text, marginBottom: 0 }}
                        />
                        <Button
                            size="sm"
                            style={{
                                backgroundColor: "transparent",
                                borderColor: theme.border,
                                color: theme.text
                            }}
                            onClick={() => this.setState(prevState => ({ isPageTableView: !prevState.isPageTableView }))}
                        >
                            {isPageTableView ? "Treemap" : "Table"}
                        </Button>
                    </div>
                </div>
                {isPageTableView ? (
                    <HeatmapMetricsTable
                        data={filteredRows}
                        emptyMessage="No source page table data available"
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        initialSortKey="plays"
                        preferredLeadingKeys={["source_page_rollup", "source_page", "page"]}
                        customerConfig={this.props.customerConfig}
                        heatmapAccentColor={CHART_PRIMARY_COLOR}
                    />
                ) : (
                    this.renderRailTreemap(treemapData, theme, selectedPageMetric)
                )}
            </WidgetCard>
        );
    };

    private getRailTreemapData = (rows: ContentRailTableRow[], metricKey: RailMetricKey): SourceTreemapNode[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mappedRows = rows.map((row: ContentRailTableRow, index: number) => {
            const plays = Number(row.plays ?? 0);
            const views = Number(row.views ?? 0);
            const users = Number(row.users ?? 0);
            const hours = Number(row.hours ?? 0);
            const avgProgressRaw = row.avg_progress;
            const playsPerUserRaw = row.plays_per_user;
            const avgProgress = avgProgressRaw === null || avgProgressRaw === undefined ? null : Number(avgProgressRaw);
            const playsPerUser = playsPerUserRaw === null || playsPerUserRaw === undefined ? null : Number(playsPerUserRaw);
            const sourceRail = String(row.source_rail ?? `Rail ${index + 1}`);
            const metricValues: Record<RailMetricKey, number | null> = {
                plays: Number.isFinite(plays) ? plays : null,
                views: Number.isFinite(views) ? views : null,
                users: Number.isFinite(users) ? users : null,
                "avg_progress": Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                "plays_per_user": Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours: Number.isFinite(hours) ? hours : null
            };

            const metricValue = metricValues[metricKey];
            const rawMetricValue = metricValue === null ? 0 : Math.max(0, metricValue);

            return {
                name: sourceRail.replace(/_/g, " "),
                value: rawMetricValue,
                rawMetricValue,
                sourceLabel: sourceRail,
                plays,
                views,
                users,
                avgProgress: Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                playsPerUser: Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours
            };
        });

        return this.applyRankBasedTreemapWeights(mappedRows);
    };

    private renderRailTreemap = (data: SourceTreemapNode[], theme: WidgetTheme, metricKey: RailMetricKey): ReactNode => {
        const hasData = data.length > 0;

        if (!hasData) {
            return <Alert variant="info" style={{ marginBottom: 0 }}>No source rail data available</Alert>;
        }

        const maxValue = data.reduce((currentMax, item) => Math.max(currentMax, Number(item.value ?? 0)), 0);
        const metricLabel = RAIL_METRIC_OPTIONS.find(option => option.key === metricKey)?.label || "Metric";
        const formatMetricValue = (value: number): string => {
            if (metricKey === "avg_progress") {
                return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
            }

            return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
        };
        const nodeByName = new Map<string, SourceTreemapNode>(data.map(item => [item.name, item]));
        const getNodeFromTreemapPayload = (payload: any, name?: string): SourceTreemapNode | null => {
            const candidates = [
                payload,
                payload?.payload,
                payload?.payload?.payload,
                payload?.data,
                payload?.node,
                payload?.node?.payload
            ];

            for (const candidate of candidates) {
                if (!candidate) {
                    continue;
                }

                const candidatePlays = Number(candidate.plays);
                const candidateName = typeof candidate.name === "string" ? candidate.name : undefined;

                if (Number.isFinite(candidatePlays) || candidateName) {
                    return candidate as SourceTreemapNode;
                }
            }

            if (name && nodeByName.has(name)) {
                return nodeByName.get(name) ?? null;
            }

            return null;
        };

        const truncateLabel = (value: string, width: number): string => {
            const chars = Math.max(8, Math.floor(width / 8));
            if (value.length <= chars) {
                return value;
            }

            return `${value.slice(0, Math.max(0, chars - 1))}…`;
        };

        const treemapHeight = ContentDashboard.TREEMAP_HEIGHT;

        return (
            <ResponsiveContainer width="100%" height={treemapHeight}>
                <Treemap
                    data={data}
                    dataKey="value"
                    stroke={theme.border}
                    aspectRatio={1}
                    content={(props: any): React.ReactElement => {
                        const {
                            x,
                            y,
                            width,
                            height,
                            name,
                            payload
                        } = props;

                        if (width <= 0 || height <= 0) {
                            return <g />;
                        }

                        const node = getNodeFromTreemapPayload(payload, typeof name === "string" ? name : undefined);
                        const metricRaw = Number(node?.rawMetricValue ?? 0);
                        const metricValue = Number.isFinite(metricRaw) ? metricRaw : 0;
                        const valueForColor = Number(node?.value ?? payload?.value ?? 0);
                        const normalized = maxValue > 0 ? Math.max(0, Math.min(1, valueForColor / maxValue)) : 0;
                        const fill = `hsl(${INDIGO_HUE}, 88%, ${60 - (normalized * 28)}%)`;
                        const canRenderLabel = width > 20 && height > 14;
                        const canRenderMetric = width > 90 && height > 54;
                        const centerX = x + (width / 2);
                        const centerY = y + (height / 2);
                        const baseLabel = String(node?.name ?? name ?? "");
                        const label = truncateLabel(baseLabel, Math.max(8, width - 8));
                        const mainFontSize = Math.max(9, Math.min(20, Math.floor(Math.min(width / 6.5, height / 2.4))));
                        const valueFontSize = Math.max(12, Math.min(16, Math.floor(mainFontSize * 0.72)));
                        const clipId = `rail-cell-${Math.round(x)}-${Math.round(y)}-${Math.round(width)}-${Math.round(height)}`;
                        const textY = canRenderMetric ? centerY - (mainFontSize * 0.28) : centerY;

                        return (
                            <g>
                                <rect x={x} y={y} width={width} height={height} style={{ fill, stroke: theme.border, strokeWidth: 1 }} />
                                <clipPath id={clipId}>
                                    <rect x={x + 2} y={y + 2} width={Math.max(0, width - 4)} height={Math.max(0, height - 4)} />
                                </clipPath>
                                {canRenderLabel && (
                                    <g clipPath={`url(#${clipId})`}>
                                        <text
                                            x={centerX}
                                            y={textY}
                                            fill="#ffffff"
                                            fontSize={mainFontSize}
                                            fontWeight={800}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.38)", strokeWidth: 2 }}
                                        >
                                            {label}
                                        </text>
                                        {canRenderMetric && (
                                            <text
                                                x={centerX}
                                                y={centerY + (valueFontSize * 0.95)}
                                                fill="rgba(255,255,255,0.96)"
                                                fontSize={valueFontSize}
                                                fontWeight={700}
                                                textAnchor="middle"
                                                dominantBaseline="central"
                                                style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.32)", strokeWidth: 1.5 }}
                                            >
                                                {`${metricLabel}: ${formatMetricValue(metricValue)}`}
                                            </text>
                                        )}
                                    </g>
                                )}
                            </g>
                        );
                    }}
                >
                    <Tooltip
                        contentStyle={{
                            backgroundColor: theme.chartBg,
                            border: `1px solid ${theme.border}`,
                            color: theme.text
                        }}
                        itemStyle={{ color: theme.text }}
                        labelStyle={{ color: theme.text }}
                        formatter={(_value: number | string | Array<number | string> | undefined, _name?: string, details?: any) => {
                            const node = details?.payload as SourceTreemapNode | undefined;
                            if (!node) {
                                return ["", ""];
                            }

                            const avgProgress = node.avgProgress === null ? "-" : `${node.avgProgress.toFixed(2)}%`;
                            const playsPerUser = node.playsPerUser === null ? "-" : node.playsPerUser.toLocaleString(undefined, { maximumFractionDigits: 2 });
                            const selectedMetric = formatMetricValue(Number(node.rawMetricValue ?? 0));

                            return [
                                `${metricLabel}: ${selectedMetric} | Plays: ${node.plays.toLocaleString()} | Views: ${node.views.toLocaleString()} | Users: ${node.users.toLocaleString()} | Avg Progress: ${avgProgress} | Plays/User: ${playsPerUser} | Hours: ${node.hours.toLocaleString()}`,
                                node.name
                            ];
                        }}
                    />
                </Treemap>
            </ResponsiveContainer>
        );
    };

    private renderRailWidget = (theme: WidgetTheme): ReactNode => {
        const { railTableData, sidebarEntries, widgetStates, isRailTableView, selectedRailMetric, railScope, showEmptyRailCategories } = this.state;
        const widgetState = widgetStates.content_rail_table || { isLoading: false, error: null };
        const filteredRows = this.getScopedRowsByMetric(railTableData, selectedRailMetric, railScope, showEmptyRailCategories);
        const treemapData = this.getRailTreemapData(filteredRows, selectedRailMetric);

        return (
            <WidgetCard
                title="Source Rail Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "content_rail_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && filteredRows.length === 0}
                emptyMessage="No source rail data available for the selected date range."
            >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", gap: "12px", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Metric</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={selectedRailMetric}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ selectedRailMetric: event.target.value as RailMetricKey })}
                            style={{
                                minWidth: "190px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {RAIL_METRIC_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Range</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={railScope}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ railScope: event.target.value as TreemapScope })}
                            style={{
                                minWidth: "120px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {SOURCE_SCOPE_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <Form.Check
                            type="switch"
                            id="show-empty-rail-categories"
                            label="Show empty categories"
                            checked={showEmptyRailCategories}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ showEmptyRailCategories: event.target.checked })}
                            style={{ color: theme.text, marginBottom: 0 }}
                        />
                        <Button
                            size="sm"
                            style={{
                                backgroundColor: "transparent",
                                borderColor: theme.border,
                                color: theme.text
                            }}
                            onClick={() => this.setState(prevState => ({ isRailTableView: !prevState.isRailTableView }))}
                        >
                            {isRailTableView ? "Treemap" : "Table"}
                        </Button>
                    </div>
                </div>
                {isRailTableView ? (
                    <HeatmapMetricsTable
                        data={filteredRows}
                        emptyMessage="No source rail data available"
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        initialSortKey="plays"
                        preferredLeadingKeys={["source_rail", "source_rail_rollup", "rail"]}
                        customerConfig={this.props.customerConfig}
                        heatmapAccentColor={CHART_PRIMARY_COLOR}
                    />
                ) : (
                    this.renderRailTreemap(treemapData, theme, selectedRailMetric)
                )}
            </WidgetCard>
        );
    };

    private getDeviceTreemapData = (rows: ContentDeviceTableRow[], metricKey: RailMetricKey): SourceTreemapNode[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mappedRows = rows.map((row: ContentDeviceTableRow, index: number) => {
            const plays = Number(row.plays ?? 0);
            const views = Number(row.views ?? 0);
            const users = Number(row.users ?? 0);
            const hours = Number(row.hours ?? 0);
            const avgProgressRaw = row.avg_progress;
            const playsPerUserRaw = row.plays_per_user;
            const avgProgress = avgProgressRaw === null || avgProgressRaw === undefined ? null : Number(avgProgressRaw);
            const playsPerUser = playsPerUserRaw === null || playsPerUserRaw === undefined ? null : Number(playsPerUserRaw);
            const devicePlatform = String(row.device_platform ?? `Device ${index + 1}`);
            const metricValues: Record<RailMetricKey, number | null> = {
                plays: Number.isFinite(plays) ? plays : null,
                views: Number.isFinite(views) ? views : null,
                users: Number.isFinite(users) ? users : null,
                "avg_progress": Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                "plays_per_user": Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours: Number.isFinite(hours) ? hours : null
            };

            const metricValue = metricValues[metricKey];
            const rawMetricValue = metricValue === null ? 0 : Math.max(0, metricValue);

            return {
                name: devicePlatform,
                value: rawMetricValue,
                rawMetricValue,
                sourceLabel: devicePlatform,
                plays,
                views,
                users,
                avgProgress: Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                playsPerUser: Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours
            };
        });

        return this.applyRankBasedTreemapWeights(mappedRows);
    };

    private renderDeviceWidget = (theme: WidgetTheme): ReactNode => {
        const { deviceTableData, sidebarEntries, widgetStates, isDeviceTableView, selectedDeviceMetric, deviceScope, showEmptyDeviceCategories } = this.state;
        const widgetState = widgetStates.content_device_table || { isLoading: false, error: null };
        const filteredRows = this.getScopedRowsByMetric(deviceTableData, selectedDeviceMetric, deviceScope, showEmptyDeviceCategories);
        const treemapData = this.getDeviceTreemapData(filteredRows, selectedDeviceMetric);

        return (
            <WidgetCard
                title="Device Platform Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "content_device_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && filteredRows.length === 0}
                emptyMessage="No device platform data available for the selected date range."
            >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", gap: "12px", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Metric</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={selectedDeviceMetric}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ selectedDeviceMetric: event.target.value as RailMetricKey })}
                            style={{
                                minWidth: "190px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {RAIL_METRIC_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Range</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={deviceScope}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ deviceScope: event.target.value as TreemapScope })}
                            style={{
                                minWidth: "120px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {SOURCE_SCOPE_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <Form.Check
                            type="switch"
                            id="show-empty-device-categories"
                            label="Show empty categories"
                            checked={showEmptyDeviceCategories}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ showEmptyDeviceCategories: event.target.checked })}
                            style={{ color: theme.text, marginBottom: 0 }}
                        />
                        <Button
                            size="sm"
                            style={{
                                backgroundColor: "transparent",
                                borderColor: theme.border,
                                color: theme.text
                            }}
                            onClick={() => this.setState(prevState => ({ isDeviceTableView: !prevState.isDeviceTableView }))}
                        >
                            {isDeviceTableView ? "Treemap" : "Table"}
                        </Button>
                    </div>
                </div>
                {isDeviceTableView ? (
                    <HeatmapMetricsTable
                        data={filteredRows}
                        emptyMessage="No device platform data available"
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        initialSortKey="plays"
                        preferredLeadingKeys={["device_platform", "platform", "device"]}
                        customerConfig={this.props.customerConfig}
                        heatmapAccentColor={CHART_PRIMARY_COLOR}
                    />
                ) : (
                    this.renderRailTreemap(treemapData, theme, selectedDeviceMetric)
                )}
            </WidgetCard>
        );
    };

    private mapVideoStartRows = (result: unknown, primaryKey: "week" | "day"): GenreExplorerMetricsByTimePoint[] => {
        const rows = Array.isArray(result) ? result : [];
        return rows
            .map((row: any): GenreExplorerMetricsByTimePoint | null => {
                const key = row?.[primaryKey] ?? row?.[primaryKey === "week" ? "day" : "week"];
                if (typeof key !== "string" || key.trim() === "") {
                    return null;
                }
                const raw = row?.value ?? row?.video_start;
                const value = typeof raw === "number" && Number.isFinite(raw) ? raw : null;
                return { key, value };
            })
            .filter((item): item is GenreExplorerMetricsByTimePoint => item !== null);
    };

    private fetchVideoStartByTimeWidget = (timeView: GenreExplorerTimeView, metric: string): void => {
        const { appliedFilters } = this.state;
        const isWeekly = timeView === "weekly";
        const requestId = isWeekly ? ++this.videoStartWeeklyRequestId : ++this.videoStartDowRequestId;
        const widgetKey = isWeekly ? "content_metrics_weekly" : "content_metrics_dow";
        const slug = widgetKey;
        const primaryKey = isWeekly ? "week" : "day";

        this.setState(prevState => ({
            widgetStates: {
                ...prevState.widgetStates,
                [widgetKey]: { isLoading: true, error: null }
            }
        }));

        void (async () => {
            try {
                const response = await getAnalyticsWidget(
                    this.props.customerConfig,
                    slug,
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandName,
                    appliedFilters.devicePlatform,
                    undefined,
                    undefined,
                    { "metric_by_time": metric }
                );

                const latestId = isWeekly ? this.videoStartWeeklyRequestId : this.videoStartDowRequestId;
                if (!this.isMountedFlag || requestId !== latestId) {
                    return;
                }

                const data = this.mapVideoStartRows((response as any)?.result, primaryKey);
                this.setState(prevState => ({
                    videoStartWeeklyData: isWeekly ? data : prevState.videoStartWeeklyData,
                    videoStartDowData: isWeekly ? prevState.videoStartDowData : data,
                    widgetStates: {
                        ...prevState.widgetStates,
                        [widgetKey]: { isLoading: false, error: null }
                    }
                }));
            } catch (err) {
                const latestId = isWeekly ? this.videoStartWeeklyRequestId : this.videoStartDowRequestId;
                if (!this.isMountedFlag || requestId !== latestId) {
                    return;
                }

                Logger.error(`Error fetching ${widgetKey}`, err);
                this.setState(prevState => ({
                    widgetStates: {
                        ...prevState.widgetStates,
                        [widgetKey]: { isLoading: false, error: "Failed to load widget data" }
                    }
                }));
            }
        })();
    };

    private handleVideoStartMetricChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const metric = event.target.value;
        this.setState({ videoStartMetric: metric }, () => {
            this.fetchVideoStartByTimeWidget("weekly", metric);
            this.fetchVideoStartByTimeWidget("dow", metric);
        });
    };

    private handleVideoStartTimeViewChange = (timeView: GenreExplorerTimeView): void => {
        this.setState({ videoStartTimeView: timeView });
    };

    private renderContentVideoStartByTimeWidget = (theme: WidgetTheme): ReactNode => {
        const { videoStartWeeklyData, videoStartDowData, videoStartTimeView, videoStartMetric, isDarkTheme, widgetStates } = this.state;
        const activeWidgetKey = videoStartTimeView === "weekly" ? "content_metrics_weekly" : "content_metrics_dow";
        const widgetState = widgetStates[activeWidgetKey] || { isLoading: false, error: null };
        const metricOption = getMetricByTimeOption(videoStartMetric);
        const axisColor = theme.text;
        const axisLineColor = isDarkTheme ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)";
        const tooltipBackground = isDarkTheme ? "#1f1f1f" : "#ffffff";
        const tooltipTextColor = isDarkTheme ? "#ffffff" : "#212529";
        const tooltipBorder = isDarkTheme ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.2)";
        const activeData = videoStartTimeView === "weekly" ? videoStartWeeklyData : videoStartDowData;
        const chartRows = activeData.map(point => ({
            key: point.key,
            rawValue: point.value,
            chartValue: toChartMetricValue(point.value, metricOption.format)
        }));
        const ariaLabel = `${metricOption.label} by ${videoStartTimeView === "weekly" ? "week" : "day of week"}`;
        const formatAxisTick = (value: number): string => {
            if (metricOption.format === "conversion" || metricOption.format === "percent") {
                return `${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
            }
            if (metricOption.format === "hours" || metricOption.format === "ratio") {
                return value.toFixed(2);
            }
            return Math.round(value).toLocaleString();
        };
        const widgetTitle = videoStartTimeView === "weekly"
            ? `${metricOption.label} by Week`
            : `${metricOption.label} by Day of Week`;

        return (
            <WidgetCard
                title={widgetTitle}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && activeData.length === 0}
                emptyMessage="No data available for the selected filters and date range."
            >
                <Row style={{ marginBottom: "16px" }}>
                    <Col md={6}>
                        <Form.Group style={{ marginBottom: 0 }}>
                            <Form.Label>Metric</Form.Label>
                            <Form.Control
                                as="select"
                                value={videoStartMetric}
                                onChange={this.handleVideoStartMetricChange}
                                disabled={widgetState.isLoading}
                            >
                                {METRIC_BY_TIME_OPTIONS.map(option => (
                                    <option key={option.key} value={option.key}>{option.label}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group style={{ marginBottom: 0 }}>
                            <Form.Label>View</Form.Label>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <Button
                                    variant={videoStartTimeView === "weekly" ? "primary" : "outline-secondary"}
                                    onClick={() => this.handleVideoStartTimeViewChange("weekly")}
                                    aria-pressed={videoStartTimeView === "weekly"}
                                >
                                    Weekly
                                </Button>
                                <Button
                                    variant={videoStartTimeView === "dow" ? "primary" : "outline-secondary"}
                                    onClick={() => this.handleVideoStartTimeViewChange("dow")}
                                    aria-pressed={videoStartTimeView === "dow"}
                                >
                                    Day of Week
                                </Button>
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <div role="img" aria-label={ariaLabel}>
                    <ResponsiveContainer width="100%" height={360}>
                        {videoStartTimeView === "weekly" ? (
                            <LineChart data={chartRows} margin={{ top: 16, right: 24, left: 8, bottom: 16 }}>
                                <CartesianGrid stroke={axisLineColor} strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="key"
                                    tick={{ fill: axisColor }}
                                    axisLine={{ stroke: axisLineColor }}
                                    tickLine={{ stroke: axisLineColor }}
                                    minTickGap={20}
                                />
                                <YAxis
                                    tick={{ fill: axisColor }}
                                    axisLine={{ stroke: axisLineColor }}
                                    tickLine={{ stroke: axisLineColor }}
                                    tickFormatter={formatAxisTick}
                                />
                                <Tooltip
                                    labelFormatter={(label: any) => String(label ?? "")}
                                    formatter={(_value: number | string | undefined, _name: string | number | undefined, item: any) => {
                                        return [formatMetricByTimeValue(item?.payload?.rawValue ?? null, metricOption.format), metricOption.label];
                                    }}
                                    contentStyle={{ backgroundColor: tooltipBackground, border: tooltipBorder, color: tooltipTextColor }}
                                    labelStyle={{ color: tooltipTextColor }}
                                    itemStyle={{ color: tooltipTextColor }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="chartValue"
                                    stroke={CHART_PRIMARY_COLOR}
                                    strokeWidth={2}
                                    connectNulls={false}
                                    dot={{ r: 2 }}
                                    activeDot={{ r: 4 }}
                                />
                            </LineChart>
                        ) : (
                            <BarChart data={chartRows} margin={{ top: 16, right: 24, left: 8, bottom: 16 }}>
                                <CartesianGrid stroke={axisLineColor} strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="key"
                                    tick={{ fill: axisColor }}
                                    axisLine={{ stroke: axisLineColor }}
                                    tickLine={{ stroke: axisLineColor }}
                                />
                                <YAxis
                                    tick={{ fill: axisColor }}
                                    axisLine={{ stroke: axisLineColor }}
                                    tickLine={{ stroke: axisLineColor }}
                                    tickFormatter={formatAxisTick}
                                />
                                <Tooltip
                                    labelFormatter={(label: any) => String(label ?? "")}
                                    formatter={(_value: number | string | undefined, _name: string | number | undefined, item: any) => {
                                        return [formatMetricByTimeValue(item?.payload?.rawValue ?? null, metricOption.format), metricOption.label];
                                    }}
                                    contentStyle={{ backgroundColor: tooltipBackground, border: tooltipBorder, color: tooltipTextColor }}
                                    labelStyle={{ color: tooltipTextColor }}
                                    itemStyle={{ color: tooltipTextColor }}
                                />
                                <Bar dataKey="chartValue" fill={CHART_PRIMARY_COLOR} />
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </div>
            </WidgetCard>
        );
    };

private renderVideoStartWeeklyWidget = (theme: WidgetTheme): ReactNode => {
    const { videoStartWeeklyData, isDarkTheme, widgetStates } = this.state;
    const widgetState = widgetStates.content_metrics_weekly || { isLoading: false, error: null };
        const axisColor = theme.text;
        const axisLineColor = isDarkTheme ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)";
        const tooltipBackground = isDarkTheme ? "#1f1f1f" : "#ffffff";
        const tooltipTextColor = isDarkTheme ? "#ffffff" : "#212529";
        const tooltipBorder = isDarkTheme ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.2)";
        const chartRows = videoStartWeeklyData.map(point => ({
            key: point.key,
            rawValue: point.value,
            chartValue: point.value
        }));

        return (
            <WidgetCard
                title="Video Starts by Week"
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && videoStartWeeklyData.length === 0}
                emptyMessage="No weekly video start data available for the selected date range."
            >
                <div role="img" aria-label="Video Starts by week">
                    <ResponsiveContainer width="100%" height={360}>
                        <LineChart data={chartRows} margin={{ top: 16, right: 24, left: 8, bottom: 16 }}>
                            <CartesianGrid stroke={axisLineColor} strokeDasharray="3 3" />
                            <XAxis
                                dataKey="key"
                                tick={{ fill: axisColor }}
                                axisLine={{ stroke: axisLineColor }}
                                tickLine={{ stroke: axisLineColor }}
                                minTickGap={20}
                            />
                            <YAxis
                                tick={{ fill: axisColor }}
                                axisLine={{ stroke: axisLineColor }}
                                tickLine={{ stroke: axisLineColor }}
                                tickFormatter={(value: number) => formatMetricByTimeValue(value, "integer")}
                            />
                            <Tooltip
                                labelFormatter={(label: any) => String(label ?? "")}
                                formatter={(_value: number | string | undefined, _name: string | number | undefined, item: any) => {
                                    return [formatMetricByTimeValue(item?.payload?.rawValue ?? null, "integer"), "Video Starts"];
                                }}
                                contentStyle={{ backgroundColor: tooltipBackground, border: tooltipBorder, color: tooltipTextColor }}
                                labelStyle={{ color: tooltipTextColor }}
                                itemStyle={{ color: tooltipTextColor }}
                            />
                            <Line
                                type="monotone"
                                dataKey="chartValue"
                                stroke={CHART_PRIMARY_COLOR}
                                strokeWidth={2}
                                connectNulls={false}
                                dot={{ r: 2 }}
                                activeDot={{ r: 4 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </WidgetCard>
        );
    };

    private renderVideoStartDowWidget = (theme: WidgetTheme): ReactNode => {
        const { videoStartDowData, isDarkTheme, widgetStates } = this.state;
        const widgetState = widgetStates.content_video_start_dow || { isLoading: false, error: null };
        const axisColor = theme.text;
        const axisLineColor = isDarkTheme ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)";
        const tooltipBackground = isDarkTheme ? "#1f1f1f" : "#ffffff";
        const tooltipTextColor = isDarkTheme ? "#ffffff" : "#212529";
        const tooltipBorder = isDarkTheme ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.2)";
        const chartRows = videoStartDowData.map(point => ({
            key: point.key,
            rawValue: point.value,
            chartValue: point.value
        }));

        return (
            <WidgetCard
                title="Video Starts by Day of Week"
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && videoStartDowData.length === 0}
                emptyMessage="No day-of-week video start data available for the selected date range."
            >
                <div role="img" aria-label="Video Starts by day of week">
                    <ResponsiveContainer width="100%" height={360}>
                        <BarChart data={chartRows} margin={{ top: 16, right: 24, left: 8, bottom: 40 }}>
                            <CartesianGrid stroke={axisLineColor} strokeDasharray="3 3" />
                            <XAxis
                                dataKey="key"
                                interval={0}
                                tick={(props: any) => {
                                    const { x, y, payload } = props;
                                    return (
                                        <text
                                            x={x}
                                            y={y + 4}
                                            textAnchor="end"
                                            fill={axisColor}
                                            fontSize={12}
                                            transform={`rotate(-35, ${x}, ${y + 4})`}
                                        >
                                            {payload.value}
                                        </text>
                                    );
                                }}
                                axisLine={{ stroke: axisLineColor }}
                                tickLine={{ stroke: axisLineColor }}
                            />
                            <YAxis
                                tick={{ fill: axisColor }}
                                axisLine={{ stroke: axisLineColor }}
                                tickLine={{ stroke: axisLineColor }}
                                tickFormatter={(value: number) => formatMetricByTimeValue(value, "integer")}
                            />
                            <Tooltip
                                labelFormatter={(label: any) => String(label ?? "")}
                                formatter={(_value: number | string | undefined, _name: string | number | undefined, item: any) => {
                                    return [formatMetricByTimeValue(item?.payload?.rawValue ?? null, "integer"), "Video Starts"];
                                }}
                                contentStyle={{ backgroundColor: tooltipBackground, border: tooltipBorder, color: tooltipTextColor }}
                                labelStyle={{ color: tooltipTextColor }}
                                itemStyle={{ color: tooltipTextColor }}
                            />
                            <Bar dataKey="chartValue" fill={CHART_PRIMARY_COLOR} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </WidgetCard>
        );
    };

    private renderContentEngagementMetrics = (theme: WidgetTheme): ReactNode => {
        const { contentEngagementMetrics, sidebarEntries, widgetStates } = this.state;
        const widgetState = widgetStates.content_engagement_metrics || { isLoading: false, error: null };

        return (
            <WidgetCard
                title="Content Engagement Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "content_engagement_metrics")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && contentEngagementMetrics.length === 0}
                emptyMessage="No data for selected filters. Try widening date range or removing one or more filters."
            >
                <MetricCards
                    metrics={contentEngagementMetrics as MetricCardItem[]}
                    theme={theme}
                    hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                    emptyMessage="No data for selected filters. Try widening date range or removing one or more filters."
                    minCardWidth="200px"
                    maxCardWidth="200px"
                    gridGap="16px"
                />
            </WidgetCard>
        );
    };

    public render(): ReactNode {
        const { filters, filterOptions, appliedFilters, filtersCollapsed, isDarkTheme, error, sidebarOpen, sidebarEntries, widgetStates, contentEngagementMetrics, genreActionsData, genreUsersData } = this.state;
        const theme = getWidgetTheme(isDarkTheme);
        const charts = this.getChartsConfig();
        const catalogueCharts = this.getCatalogueChartsConfig();
        const shareCharts = charts.filter(chart => ["share_actions", "share_users", "share_content", "share_brands"].includes(chart.key));
        const riserCharts = charts.filter(chart => ["content_risers_fallers", "content_recommended_risers_fallers", "content_new_risers", "content_percentage_risers_fallers"].includes(chart.key));
        const allWidgetsSettled = Object.values(widgetStates).every(state => !state.isLoading);

        return (
            <div style={{ display: "flex", alignItems: "flex-start", backgroundColor: isDarkTheme ? "#060606" : "#ffffff", color: isDarkTheme ? "#ffffff" : "#000000", minHeight: "100vh" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
            <Container className="mw-100" key="content-dashboard" style={{ position: "relative", paddingBottom: "40px" }}>
                <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                    <DataFreshnessBanner customerConfig={this.props.customerConfig} isDarkTheme={isDarkTheme} />
                    <ExportButton
                        context={this.getExportContext()}
                        enabled={allWidgetsSettled && !error && (contentEngagementMetrics.length > 0 || genreActionsData.length > 0 || genreUsersData.length > 0)}
                        scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                        onScheduleClick={() => this.setState({ showScheduleModal: true })}
                    />
                    <Button
                        onClick={this.toggleTheme}
                        style={{
                            backgroundColor: "transparent",
                            border: "none",
                            fontSize: "24px",
                            padding: "4px 8px",
                            color: isDarkTheme ? "#ffffff" : "#000000"
                        }}
                        title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                    >
                        {isDarkTheme ? <Sun /> : <Moon />}
                    </Button>
                    <Button
                        onClick={() => this.setState(prevState => ({ filtersCollapsed: !prevState.filtersCollapsed }))}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                        title={filtersCollapsed ? "Show filters" : "Hide filters"}
                    >
                        {filtersCollapsed ? <Funnel /> : <FunnelFill />}
                    </Button>
                    <Button
                        onClick={this.toggleSidebar}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                        title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                    >
                        <BookHalf />
                    </Button>
                </div>

                <Row>
                    <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                        <h2 style={{ color: theme.text }}>Content Dashboard</h2>
                    </Col>
                </Row>

                <AnalyticsFiltersComponent
                    filters={filters}
                    filterOptions={filterOptions}
                    onFilterChange={this.handleFilterChange}
                    onApplyFilters={this.handleApplyFilters}
                    customerConfig={this.props.customerConfig}
                    enableGroupBy={{}}
                    isCollapsed={filtersCollapsed}
                />
                <PopPeriodBanner startDate={appliedFilters.startDate} endDate={appliedFilters.endDate} />

                {error && (
                    <Row>
                        <Col>
                            <Alert variant="danger" style={{ marginTop: "20px" }}>{error}</Alert>
                        </Col>
                    </Row>
                )}
                <>
                    <Row>
                        <Col md={12} style={{ marginTop: "20px" }}>
                            {this.renderContentEngagementMetrics(theme)}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} style={{ marginTop: "20px" }}>
                            {this.renderBrandTable(theme)}
                        </Col>
                    </Row>
                    <Row>
                        {shareCharts.map(chart => (
                            <Col key={chart.key} md={12} lg={6} style={{ marginTop: "20px" }}>
                                {this.renderBarCard(chart, theme)}
                            </Col>
                        ))}
                    </Row>
                    <Row>
                        <Col md={12} style={{ marginTop: "20px" }}>
                            {this.renderGenreTable(theme)}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} style={{ marginTop: "20px" }}>
                            {this.renderGenreRadarWidget(theme)}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} style={{ marginTop: "20px" }}>
                            {this.renderPageWidget(theme)}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} style={{ marginTop: "20px" }}>
                            {this.renderRailWidget(theme)}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} style={{ marginTop: "20px" }}>
                            {this.renderDeviceWidget(theme)}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} style={{ marginTop: "20px" }}>
                            {this.renderContentVideoStartByTimeWidget(theme)}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} style={{ marginTop: "28px", textAlign: "center" }}>
                            <h4 style={{ color: theme.text, marginBottom: "8px" }}>Overall Catalogue Metrics</h4>
                            <p style={{ color: theme.text, opacity: 0.8, marginBottom: 0 }}>Filters do not apply to this section</p>
                        </Col>
                    </Row>
                    <Row>
                        {catalogueCharts.map(chart => (
                            <Col key={chart.key} md={12} lg={6} style={{ marginTop: "20px" }}>
                                {this.renderBarCard(chart, theme)}
                            </Col>
                        ))}
                    </Row>
                    <Row>
                        {riserCharts.map(chart => (
                            <Col key={chart.key} md={12} lg={6} style={{ marginTop: "20px" }}>
                                {this.renderBarCard(chart, theme)}
                            </Col>
                        ))}
                    </Row>
                </>
            </Container>
            </div>
            <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            <ScheduleExportModal
                show={this.state.showScheduleModal}
                onHide={() => this.setState({ showScheduleModal: false })}
                filters={this.state.appliedFilters}
                pageKey="content_dashboard"
                pageTitle="Content Dashboard"
                customerSlug={this.props.customerConfig.slug}
                customerConfig={this.props.customerConfig}
                editingSchedule={this.state.editingSchedule}
                onSave={this.handleScheduleSave}
            />
            </div>
        );
    }
}

export default ContentDashboard;
